import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router";
import { PASSWORD_MAX, PASSWORD_MIN, newPasswordProblem, resetPassword, type AuthResult } from "../../auth/authClient";
import { PageShell } from "../../ui/PageShell";

/**
 * `/reset-password?token=...`: the landing page of the password reset email (ADR-0005). Better Auth
 * validates the token; an invalid or expired link arrives as `?error=INVALID_TOKEN`. The new
 * password is checked here for length and confirmation, and again by the server.
 */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  return <ResetPasswordView token={params.get("token") ?? ""} linkError={params.get("error")} submit={resetPassword} />;
}

const INVALID_LINK = "This reset link is invalid or has expired. Request a new one from the sign-in page.";

export function ResetPasswordView({
  token,
  linkError,
  submit,
}: {
  token: string;
  linkError: string | null;
  submit(token: string, newPassword: string): Promise<AuthResult>;
}) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const problem = newPasswordProblem(password, confirm);
  const invalidLink = !token || Boolean(linkError);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (problem || busy) return;
    setBusy(true);
    setError(null);
    const res = await submit(token, password);
    setBusy(false);
    if (res.ok) {
      setDone(true);
      setPassword("");
      setConfirm("");
      return;
    }
    setError(res.code === "INVALID_TOKEN" || /invalid token|expired/i.test(res.message ?? "") ? INVALID_LINK : (res.message ?? "Your password could not be changed. Try again."));
  };

  return (
    <PageShell title="Reset your password" narrow>
      <div className="auth-card" data-testid="reset-password">
        {done ? (
          <div className="notice notice-success" role="status" data-testid="reset-done">
            Your password was changed. <Link to="/account">Sign in with your new password</Link>.
          </div>
        ) : invalidLink ? (
          <div className="notice notice-danger" role="alert" data-testid="reset-invalid">
            {INVALID_LINK} <Link to="/account?forgot=1">Request a new link</Link>.
          </div>
        ) : (
          <form className="auth-forms" onSubmit={(e) => void onSubmit(e)} noValidate data-movement-block="true">
            <p className="hint" style={{ margin: 0 }}>
              Choose a new password for your Townsy account ({PASSWORD_MIN}–{PASSWORD_MAX} characters).
            </p>
            <label className="field">
              New password
              <input
                type="password"
                name="newPassword"
                autoComplete="new-password"
                required
                minLength={PASSWORD_MIN}
                maxLength={PASSWORD_MAX}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-invalid={touched && Boolean(problem)}
                aria-describedby="reset-problem"
                data-testid="reset-new"
              />
            </label>
            <label className="field">
              Confirm new password
              <input
                type="password"
                name="confirmPassword"
                autoComplete="new-password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                aria-invalid={touched && Boolean(problem)}
                aria-describedby="reset-problem"
                data-testid="reset-confirm"
              />
            </label>
            <span id="reset-problem" className={touched && problem ? "hint error-text" : "hint"} data-testid="reset-problem">
              {touched && problem ? problem : `At least ${PASSWORD_MIN} characters; both fields must match.`}
            </span>
            {error ? (
              <div className="notice notice-danger" role="alert" data-testid="reset-error">
                {error} {error === INVALID_LINK ? <Link to="/account?forgot=1">Request a new link</Link> : null}
              </div>
            ) : null}
            <button type="submit" className="btn btn-primary" disabled={busy} data-testid="reset-submit">
              {busy ? "Saving…" : "Set new password"}
            </button>
          </form>
        )}
      </div>
      <p style={{ marginTop: 20 }}>
        <Link to="/">Back to the town</Link> · <Link to="/account">Account</Link>
      </p>
    </PageShell>
  );
}
